import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";

import "./JumboMovimientosChart.css";

import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  ReferenceLine
} from "recharts";

import {
  InfoCard,
  InfoContent,
  InfoText
} from "../../../../components/atoms/InfoCard";
import { HomeIcon } from "../../../../components/icons/HomeIcon";
import { DateRangeContext } from "../context/DateRangeContext";
import { RefreshContext } from "../context/RefreshContext";
import { useZoom } from "../context/ZoomContext";
import { getJumboInfo } from "../lib/utils";
import { MovimientosChartErrorBoundary } from "./BoundaryErrors/MovimientosChart";
import { SkeletonLineChart } from "./SkeletonLineChart";
import { API_GATEWAY_ENDPOINT } from "../../../../constants/api";

function CustomTooltip({ payload }) {
  const { t } = useTranslation();

  if (!payload || payload.length === 0) return null;

  const data = payload[0]?.payload;

  return (
    <section className="custom-tooltip select-none">
      <p>
        {t("ubicacion")}: <span>{data.descripcion}</span>
      </p>
      <p>
        {t("fecha")}: <span>{data.fecha_inicial}</span>
      </p>
      <p>
        {t("hora_inicial")}: <span>{data.hora_inicial}</span>
      </p>
      <p>
        {t("hora_final")}: <span>{data.hora_final}</span>
      </p>
    </section>
  );
}

const getLimiteTurnoLabel = (fecha, esTurnoDia, t, i18n, formatoReducido = false) => {
  const fechaActual = new Date(fecha);
  const diaActual = fechaActual.getDate();
  const mesActual = fechaActual.toLocaleString(i18n.language, { 
    month: formatoReducido ? 'numeric' : 'long'
  });
  
  if (formatoReducido) {
    return `${diaActual}${esTurnoDia ? 'D' : 'N'}`;
  } else {
    return `${diaActual} ${mesActual} - ${esTurnoDia ? t('dia') : t('noche')}`;
  }
};

export function JumboMovimientosChart() {
  const [zoomData, setZoomData] = useState([]);
  const [data, setData] = useState([]);
  const [refAreaLeft, setRefAreaLeft] = useState(null);
  const [refAreaRight, setRefAreaRight] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { zoomRange, setZoomRange } = useZoom();
  const { finalDate } = useContext(DateRangeContext);
  const { refreshKey } = useContext(RefreshContext);

  const { startDate, endDate } = finalDate;

  const { idMaquina, turnos } = getJumboInfo();

  const { t, i18n } = useTranslation();

  useEffect(() => {
    async function getDataMovimientos() {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(
          API_GATEWAY_ENDPOINT+"jumbo/get-movimientos-rango-fechas",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Api-Key": `${process.env.REACT_APP_API_KEY}`
            },
            body: JSON.stringify({
              idMaquina: idMaquina,
              fechaInicio: startDate,
              fechaFinal: endDate
            })
          }
        );

        const { data, message, status } = await response.json();

        if (status === "success") {
          const now = new Date();
          const isToday = endDate === now.toISOString().split("T")[0];
          
          // Fecha mínima: startDate + 07:00 Chile (UTC-3)
          const minDate = new Date(`${startDate}T07:00:00`);
          
          // Fecha máxima según la lógica especial
          let maxDate;
          if (isToday) {
            // Si es hoy, usar la hora actual
            maxDate = now;
          } else {
            // Si no es hoy, usar las 07:00 del día siguiente
            const nextDay = new Date(endDate);
            nextDay.setDate(nextDay.getDate() + 1);
            maxDate = new Date(`${nextDay.toISOString().split("T")[0]}T07:00:00`);
          }

          const processedData = data
            .map((item) => {
              // Crear fecha combinando fecha_inicial y hora_inicial
              const itemDate = new Date(`${item.fecha_inicial}T${item.hora_inicial}`);
              return { 
                ...item, 
                datetime: itemDate.getTime()
              };
            })
            .filter(item => {
              const itemDate = new Date(item.datetime);
              const isAfterMin = itemDate >= minDate;
              const isBeforeMax = itemDate <= maxDate;
              
              return isAfterMin && isBeforeMax;
            });

          setData(processedData);
          setZoomData(processedData);
        }

        if (status === "no-data" || status === "error") {
          setError(message);
        }
      } catch (err) {
        console.error("Error processing data:", err);
        setError("Hubo un error al intentar obtener los datos.");
      } finally {
        setLoading(false);
      }
    }
  
    getDataMovimientos();
  }, [startDate, endDate, idMaquina, refreshKey]);
  
  useEffect(() => {
    if (zoomRange) {
      const [min, max] = zoomRange;
      const filtered = data.filter(
        (item) => item.datetime >= min && item.datetime <= max
      );
      setZoomData(filtered);
    } else {
      setZoomData(data);
    }
  }, [zoomRange, data]);

  const zoom = useCallback(() => {
    if (refAreaLeft === refAreaRight || refAreaRight === null) {
      setRefAreaLeft(null);
      setRefAreaRight(null);

      return;
    }

    const left = Math.min(refAreaLeft, refAreaRight);
    const right = Math.max(refAreaLeft, refAreaRight);

    const filteredData = data.filter(
      (item) => item.datetime >= left && item.datetime <= right
    );

    if (filteredData.length > 0) {
      setZoomData(filteredData);
      setZoomRange([left, right]);
    }

    setRefAreaLeft(null);
    setRefAreaRight(null);
  }, [data, refAreaLeft, refAreaRight, setZoomRange]);

  const resetZoom = () => {
    setZoomData(data);
    setZoomRange(null);
  };

  const sortedLevels = useMemo(() => {
    const uniqueLevels = Array.from(
      new Set(data.map((item) => item.descripcion))
    );

    const orderedLevels = uniqueLevels.sort((a, b) => {
      const orderA = data.find((d) => d.descripcion === a)?.orden;
      const orderB = data.find((d) => d.descripcion === b)?.orden;

      return orderA - orderB;
    });

    return orderedLevels;
  }, [data]);

  const adjustDomain = (dataMin, dataMax) => {
    const now = new Date();
    const isEndDateToday = endDate === now.toISOString().split("T")[0];
    
    // Fecha mínima siempre es 07:00 del día inicial
    const adjustedMin = new Date(`${startDate}T07:00:00`);
    
    // Fecha máxima según la lógica especial
    let adjustedMax;
    if (isEndDateToday) {
      // Si es hoy, usar la hora actual
      adjustedMax = now;
    } else {
      // Si no es hoy, usar las 07:00 del día siguiente
      const nextDay = new Date(endDate);
      nextDay.setDate(nextDay.getDate() + 1);
      adjustedMax = new Date(`${nextDay.toISOString().split("T")[0]}T07:00:00`);
    }

    if (zoomRange !== null) {
      const [min, max] = zoomRange;
      return [min, max];
    }

    return [adjustedMin.getTime(), adjustedMax.getTime()];
  };

  const getTurnosReferenceLines = useMemo(() => {
    if (!data.length || !turnos?.Dia || !turnos?.Noche) return [];
    
    const referenceLines = [];
    const startDateObj = new Date(`${startDate}T00:00:00`);
    const now = new Date();
    const isToday = endDate === now.toISOString().split("T")[0];
    
    let currentDate = new Date(startDateObj);
    const endDateObj = isToday 
      ? now 
      : new Date(new Date(endDate).setDate(new Date(endDate).getDate() + 1));

    // Calcular el número de días en el rango
    const diasEnRango = Math.ceil((endDateObj - startDateObj) / (1000 * 60 * 60 * 24));
    
    // Ajustar los umbrales para el modo de visualización
    const modoVisualizacion = diasEnRango <= 5 ? 'completo' 
                           : diasEnRango <= 10 ? 'alternado'
                           : 'reducido';

    // Extraer las horas de los turnos
    const [horaDia] = turnos.Dia.hora_inicio.split(':').map(Number);
    const [horaNoche] = turnos.Noche.hora_inicio.split(':').map(Number);

    while (currentDate < endDateObj) {
      const diaActual = currentDate.getDate();
      
      // Lógica mejorada para mostrar días
      const mostrarDia = modoVisualizacion === 'completo' ||
                        (modoVisualizacion === 'alternado' && diaActual % 2 === 0) ||
                        (modoVisualizacion === 'reducido' && (
                          diaActual === 1 || 
                          diaActual % 7 === 0 || 
                          currentDate.getDate() === endDateObj.getDate()
                        ));

      if (mostrarDia) {
        // Turno día
        const turnoDiaDate = new Date(currentDate);
        turnoDiaDate.setHours(horaDia, 0, 0, 0);
        
        if (turnoDiaDate >= startDateObj && turnoDiaDate <= endDateObj) {
          referenceLines.push({
            timestamp: turnoDiaDate.getTime(),
            label: getLimiteTurnoLabel(turnoDiaDate, true, t, i18n, modoVisualizacion === 'reducido'),
            isTurnoDia: true,
            isStartOfDay: true,
            position: 'top'
          });
        }
        
        // Turno noche
        const turnoNocheDate = new Date(currentDate);
        turnoNocheDate.setHours(horaNoche, 0, 0, 0);
        
        if (turnoNocheDate >= startDateObj && turnoNocheDate <= endDateObj) {
          referenceLines.push({
            timestamp: turnoNocheDate.getTime(),
            label: getLimiteTurnoLabel(turnoNocheDate, false, t, i18n, modoVisualizacion === 'reducido'),
            isTurnoDia: false,
            isStartOfDay: false,
            position: 'top'
          });
        }
      }

      // Avanzar al siguiente día
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return referenceLines;
  }, [data, startDate, endDate, t, i18n, turnos]);

  return (
    <div>
      {loading && <SkeletonLineChart />}
      {!loading && !error && data.length > 0 && (
        <section style={{ position: "relative", paddingBlock: "15px" }}>
          <button
            className="btn-zoom-reset"
            onClick={resetZoom}
            style={{ marginBottom: "20px" }}>
            <HomeIcon />
          </button>
          <ResponsiveContainer
            style={{ position: "relative" }}
            width="100%"
            height={755}>
            <LineChart
              data={zoomData}
              onMouseDown={(event) => {
                if (event && event.activePayload) {
                  setRefAreaLeft(event.activePayload[0].payload.datetime)
                }
              }}
              onMouseMove={(event) => {
                if (refAreaLeft && event && event.activePayload) {
                  setRefAreaRight(event.activePayload[0].payload.datetime)
                }
              }}
              onMouseUp={zoom}
              margin={{
                top: 45,
                bottom: 60,
                left: 80,
                right: 10
              }}>
              <XAxis
                style={{
                  userSelect: "none"
                }}
                dataKey="datetime"
                type="number"
                scale="time"
                ticks={(() => {
                  if (!zoomData.length) return [];
                  
                  const minTimeGap = 15 * 60 * 1000; // 15 minutos en milisegundos
                  const selectedTicks = new Set();
                  let lastSelectedTime = null;
                  
                  // Siempre incluir el primer punto
                  selectedTicks.add(zoomData[0].datetime);
                  lastSelectedTime = zoomData[0].datetime;
                  
                  // Procesar puntos intermedios
                  for (let i = 1; i < zoomData.length - 1; i++) {
                    const currentTime = zoomData[i].datetime;
                    const nextTime = zoomData[i + 1].datetime;
                    
                    // Si hay un gap significativo antes o después del punto actual
                    const gapBefore = currentTime - lastSelectedTime;
                    const gapAfter = nextTime - currentTime;
                    
                    if (gapBefore >= minTimeGap || gapAfter >= minTimeGap) {
                      selectedTicks.add(currentTime);
                      lastSelectedTime = currentTime;
                    }
                    // Si el punto representa un cambio de ubicación
                    else if (zoomData[i].descripcion !== zoomData[i - 1].descripcion) {
                      selectedTicks.add(currentTime);
                      lastSelectedTime = currentTime;
                    }
                  }
                  
                  // Siempre incluir el último punto
                  selectedTicks.add(zoomData[zoomData.length - 1].datetime);
                  
                  return Array.from(selectedTicks).sort((a, b) => a - b);
                })()}
                domain={([dataMin,dataMax]) => adjustDomain(dataMin, dataMax)}
                tickFormatter={(tick) => {
                  const date = new Date(tick);
                  const day = date.getDate().toString().padStart(2, '0');
                  const month = (date.getMonth() + 1).toString().padStart(2, '0');
                  const hours = date.getHours().toString().padStart(2, '0');
                  const minutes = date.getMinutes().toString().padStart(2, '0');
                  
                  return `${month}-${day} ${hours}:${minutes}`;
                }}
              />
              <CartesianGrid 
                horizontal={true}
                vertical={false}
                strokeDasharray="3 3" 
                stroke="#E5E5E5"
              />
              <YAxis
                style={{
                  userSelect: "none"
                }}
                ticks={sortedLevels}
                domain={sortedLevels}
                scale="point"
                interval={0}
                type="category"
                dataKey="descripcion"
                padding={{
                  top: 20,
                  bottom: 20
                }}
                tickMargin={2}
                tick={{ whiteSpace: "nowrap", width: 120 }}
              />
              <Line
                dot={true}
                strokeWidth={2}
                type="monotone"
                stroke="#4dc8d1"
                dataKey="descripcion"
              />
              {<Tooltip content={<CustomTooltip />} />}
              {refAreaLeft && refAreaRight && (
                <ReferenceArea
                  x1={refAreaLeft}
                  x2={refAreaRight}
                  strokeOpacity={0.3}
                />
              )}
              {getTurnosReferenceLines.map((refLine, index) => {
                const labelOffset = 15
                
                return (
                  <ReferenceLine
                    key={index}
                    x={refLine.timestamp}
                    stroke={refLine.isTurnoDia ? "#3474E0" : "#5E35B1"}
                    strokeOpacity={0.6}
                    strokeDasharray="3 3"
                    strokeWidth={1.5}
                    label={{
                      value: refLine.label,
                      position: 'top',
                      fill: refLine.isTurnoDia ? "#3474E0" : "#5E35B1",
                      fontSize: 11,
                      fontWeight: "bold",
                      fontStyle: "italic",
                      opacity: 0.9,
                      offset: labelOffset,
                      style: {
                        userSelect: "none",
                        pointerEvents: "none"
                      }
                    }}
                    ifOverflow="extendDomain"
                  />
                );
              })}
            </LineChart>
          </ResponsiveContainer>
        </section>
      )}
      <section
        style={{
          width: "100%",
          marginBlockStart: "30px",
          fontSize: "16px"
        }}>
        {error !== null && (
          <MovimientosChartErrorBoundary height="500px">
            <InfoCard>
              <InfoContent
                style={{
                  background: "#fff",
                  padding: "1.5rem 1.85rem",
                  border: "0.5px solid #e9e8f4",
                  borderRadius: "5px",
                  boxShadow: "0px 0px 3px rgba(0, 0, 0, 0.1)"
                }}>
                <InfoText color="primary">{t(error)}</InfoText>
              </InfoContent>
            </InfoCard>
          </MovimientosChartErrorBoundary>
        )}
      </section>
    </div>
  );
}
